import { useTranslation } from "@fmc-digital/i18n";
import { Button, cn } from "@fmc-digital/ui";
import { useNavigate } from "react-router-dom";
import { config } from "@/config";
import { ROUTES } from "@/constants/routes.constants";
import { trackDownloadAppEvent, DownloadAppEvent } from "@/utils/analytics.utils";
import {
  downloadAppRootStyles,
  downloadAppHeadlineStyles,
  downloadAppCardStyles,
  downloadAppQrImageStyles,
  downloadAppQrLabelStyles,
  downloadAppCtaRowStyles,
  downloadAppQrSectionStyles,
  downloadAppMobileStoreSectionStyles,
  downloadAppStoreBadgeLinkStyles,
  downloadAppStoreBadgeImageStyles,
  downloadAppCtaButtonStyles,
} from "./download-app.styles";
import { useIsMinWidth768 } from "./useIsMinWidth768";

export interface DownloadAppContainerProps {
  readonly onContinueClick?: () => void;
}

/**
 * Download App Container
 * Tablet/desktop (≥768px): QR code in a card. Mobile: App Store / Google Play badges (Figma).
 */
export function DownloadAppContainer({ onContinueClick }: DownloadAppContainerProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isWideLayout = useIsMinWidth768();

  const handleDownloadAppClick = () => {
    trackDownloadAppEvent(DownloadAppEvent.DOWNLOAD_APP_CLICKED, {
      description: "When 'Download the App' button is clicked on value propositions.",
    });
  };

  const handleWebContinueClick = () => {
    trackDownloadAppEvent(DownloadAppEvent.WEB_CONTINUE_CLICKED, {
      description: "When 'Continue on web' button is clicked on download app screen.",
    });
    if (onContinueClick) {
      onContinueClick();
    } else {
      navigate(ROUTES.REGISTER_CREATE_ACCOUNT);
    }
  };

  const headline = t("auth.downloadApp.headline");
  const qrLabel = t("auth.downloadApp.qrLabel");
  const ctaLabel = t("auth.downloadApp.ctaLabel");
  const qrAlt = t("auth.downloadApp.qrAlt");
  const appStoreBadgeAlt = t("auth.downloadApp.appStoreBadgeAlt");
  const googlePlayBadgeAlt = t("auth.downloadApp.googlePlayBadgeAlt");

  const storeListingUrl = config.env("mobileAppDownloadUrl") || "https://smart.link/zbfpvemh0jhtj";
  const cdnRootUrl = config.env("cdnRootUrl") || "";
  const qrCodeImageUrl = cdnRootUrl ? `${cdnRootUrl}/images/servicingv2/qr-code.png` : "/images/download-app/qr-code.png";
  const appStoreBadgeUrl = cdnRootUrl ? `${cdnRootUrl}/images/servicingv2/badge-app-store.png` : "/images/download-app/badge-app-store.png";
  const googlePlayBadgeUrl = cdnRootUrl ? `${cdnRootUrl}/images/servicingv2/badge-google-play.png` : "/images/download-app/badge-google-play.png";

  const continueButton = (
    <Button
      onClick={handleWebContinueClick}
      variant="text"
      size="small"
      className={cn(downloadAppCtaButtonStyles())}
      aria-label={ctaLabel}
    >
      {ctaLabel}
    </Button>
  );

  return (
    <div className={cn(downloadAppRootStyles())} data-testid="download-app-container">
      <h1 className={cn(downloadAppHeadlineStyles())}>{headline}</h1>

      {isWideLayout ? (
        <div className={cn(downloadAppCardStyles())}>
          <div className={cn(downloadAppQrSectionStyles())}>
            <img
              src={qrCodeImageUrl}
              alt={qrAlt}
              className={cn(downloadAppQrImageStyles())}
            />
            <p className={cn(downloadAppQrLabelStyles())}>{qrLabel}</p>
          </div>

          <div className={cn(downloadAppCtaRowStyles())}>{continueButton}</div>
        </div>
      ) : (
        <div className="flex w-full flex-col items-center gap-6">
          <div className={cn(downloadAppMobileStoreSectionStyles())}>
            <a
              href={storeListingUrl}
              target="_blank"
              rel="noopener noreferrer"
              className={cn(downloadAppStoreBadgeLinkStyles())}
              aria-label={appStoreBadgeAlt}
              onClick={handleDownloadAppClick}
            >
              <img
                src={appStoreBadgeUrl}
                alt={appStoreBadgeAlt}
                width={204}
                height={68}
                className={cn(downloadAppStoreBadgeImageStyles())}
              />
            </a>
            <a
              href={storeListingUrl}
              target="_blank"
              rel="noopener noreferrer"
              className={cn(downloadAppStoreBadgeLinkStyles())}
              aria-label={googlePlayBadgeAlt}
              onClick={handleDownloadAppClick}
            >
              <img
                src={googlePlayBadgeUrl}
                alt={googlePlayBadgeAlt}
                width={204}
                height={68}
                className={cn(downloadAppStoreBadgeImageStyles())}
              />
            </a>
          </div>
          <div className={cn(downloadAppCtaRowStyles())}>{continueButton}</div>
        </div>
      )}
    </div>
  );
}
